import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ConfirmationDialogComponent,
  ConfirmDialogData,
} from '@lockton-apps/shared/ui/lui';
import { MatDialog } from '@angular/material/dialog';
import { ProposalsHooksService } from '../proposals-hooks.service';
import {
  ApplicationHooksData,
  Proposal,
} from '@lockton-apps/shared/interfaces';
import { ApplicationData } from '@lockton-apps/shared/interfaces';
import { FormGroup } from '@angular/forms';

export interface NorwayHooksData {
  isBroker: boolean;
  reSubmit: boolean;
  clientName: string;
}

@Injectable()
export class NorwayApplicationHooksService extends ProposalsHooksService<NorwayHooksData> {
  override data: ApplicationHooksData = {
    deactivateDialog: this.deactivateDialogData,
  };

  private readonly beforeSubmitDialogData = {
    broker: () => ({
      title: $localize`Are you sure?`,
      content: $localize`<p class="mb-2">
                You have made some changes to <strong>${this.meta.clientName}</strong> proposal form. Once submitted the client will receive a quote and updated version of the proposal form.
              </p>`,
      confirmButtonText: $localize`Yes, Continue`,
      cancelButtonText: $localize`Cancel`,
    }),
    contact: () => ({
      title: $localize`Are you sure?`,
      content: $localize`<p class="mb-2">
                  You have made some changes to your proposal form and are about to re-submit.
                  <strong>
                    Please ensure all details edited are correct to your knowledge.
                  </strong>
                </p>
                <p>
                  Once you have re-submitted the proposal form your broker will review the
                  changes and send an updated application and quote.
                </p>`,
      confirmButtonText: $localize`Yes, Re-submit`,
      cancelButtonText: $localize`Cancel`,
    }),
  };

  private beforeReleaseDialogData(): ConfirmDialogData {
    return {
      content: $localize`<p class="mb-2">
                You are releasing the proposal form back to <strong>${this.meta.clientName}</strong> to allow them to make updates.
              </p>`,
      confirmButtonText: $localize`Yes, Continue`,
      cancelButtonText: $localize`Cancel`,
    };
  }

  constructor(private readonly dialog: MatDialog) {
    super();
  }

  async beforeRelease(): Promise<void> {
    const isConfirmed: boolean = await this.openConfirmationDialog(
      this.beforeReleaseDialogData()
    );

    return isConfirmed ? Promise.resolve() : Promise.reject();
  }

  async beforeSubmit(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal
  ): Promise<Proposal> {
    const requiresConfirmation: boolean =
      (this.meta.isBroker && form.dirty) || this.meta.reSubmit;
    const isConfirmed: boolean =
      !requiresConfirmation ||
      (await this.openConfirmationDialog(
        this.beforeSubmitDialogData[this.meta.isBroker ? 'broker' : 'contact']()
      ));

    return isConfirmed ? Promise.resolve(proposal) : Promise.reject();
  }

  private openConfirmationDialog(data: ConfirmDialogData): Promise<boolean> {
    return firstValueFrom(
      this.dialog.open(ConfirmationDialogComponent, { data }).afterClosed()
    );
  }
}
